import React, { useMemo, useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { InsightCard } from '../../../components/admin';

const RANGE_OPTIONS = [7, 14, 30];

const parseEventDate = (event) => {
  const value = event.timestamp || event.detected_at || event.created_at;
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildTimelineData = (events, rangeDays) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(today.getDate() - (rangeDays - 1));

  const counts = new Map();
  for (let offset = 0; offset < rangeDays; offset += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + offset);
    const key = date.toISOString().split('T')[0];
    counts.set(key, 0);
  }

  events.forEach((event) => {
    const date = parseEventDate(event);
    if (!date) return;
    if (date < start || date > today) return;
    const key = date.toISOString().split('T')[0];
    if (counts.has(key)) {
      counts.set(key, counts.get(key) + 1);
    }
  });

  const data = [];
  counts.forEach((value, key) => {
    const label = new Date(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    data.push({ date: label, falls: value });
  });

  return data;
};

const FallTimelineCard = ({ events = [] }) => {
  const [range, setRange] = useState(RANGE_OPTIONS[0]);

  const chartData = useMemo(() => buildTimelineData(events, range), [events, range]);
  const totalFalls = useMemo(
    () => chartData.reduce((total, item) => total + item.falls, 0),
    [chartData],
  );
  const hasData = chartData.some((item) => item.falls > 0);

  const handleRangeChange = (_event, nextRange) => {
    if (nextRange) {
      setRange(nextRange);
    }
  };

  return (
    <InsightCard
      title={`Falls overview (last ${range} days)`}
      description="Review daily fall counts to spot spikes and trends across your sites."
      badge={{
        icon: <WarningAmberIcon />,
        label: `${totalFalls} incidents`,
        color: totalFalls > 0 ? 'warning' : 'default',
      }}
      actions={(
        <ToggleButtonGroup
          value={range}
          exclusive
          size="small"
          onChange={handleRangeChange}
        >
          {RANGE_OPTIONS.map((option) => (
            <ToggleButton key={option} value={option} sx={{ px: 2 }}>
              {option}d
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      isEmpty={!hasData}
      emptyProps={{
        title: `No incidents recorded in the last ${range} days.`,
        description: 'Live events will appear here as soon as a fall is detected.',
      }}
    >
      <Box sx={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fill: '#64748B', fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="falls" fill="#7A3BC1" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </InsightCard>
  );
};

export default FallTimelineCard;
